/**
 * Main App component
 */

import { useCallback, useMemo, useState } from 'react';
import { Header } from './components/Header';
import { ControlPanel } from './components/ControlPanel';
import { DashboardTable } from './components/DashboardTable';
import { AnalysisFilters, FilterBar } from './components/FilterBar';
import { KeychainMutePanel } from './components/KeychainMutePanel';
import { Footer } from './components/Footer';
import { useCrossChainAnalysis } from './hooks/useCrossChainAnalysis';
import { fetchMutedAuthors } from './api/crossChainApi';

const DEFAULT_FILTERS: AnalysisFilters = {
  query: '',
  steemit: 'all',
  blurt: 'all',
  minMatch: 'all',
  hideMuted: false,
};

function matchesStatus(
  status: { active: boolean; error: boolean },
  filter: AnalysisFilters['steemit']
) {
  if (filter === 'all') return true;
  if (filter === 'error') return status.error;
  if (filter === 'active') return status.active && !status.error;
  return !status.active && !status.error;
}

function App() {
  const { data, loading, error, analyze, cacheInfo } = useCrossChainAnalysis();
  const [filters, setFilters] = useState<AnalysisFilters>(DEFAULT_FILTERS);
  const [keychainUsername, setKeychainUsername] = useState('');
  const [mutingAuthor, setMutingAuthor] = useState<string | null>(null);
  const [muteStatusMessage, setMuteStatusMessage] = useState<string | null>(null);
  const [mutedAuthors, setMutedAuthors] = useState<Set<string>>(new Set());

  const keychainAvailable =
    typeof window !== 'undefined' && typeof window.hive_keychain !== 'undefined';
  const canMute = keychainAvailable && keychainUsername.trim().length > 0;

  const handleAnalyze = useCallback(async () => {
    const username = keychainUsername.trim().replace(/^@/, '').toLowerCase();

    if (!username) {
      setMuteStatusMessage('Enter your Hive username first to fetch posts and check your muted list.');
      return;
    }

    try {
      setMuteStatusMessage(`Loading muted list for @${username}...`);
      const muted = await fetchMutedAuthors(username);
      setMutedAuthors(muted);
      setMuteStatusMessage(
        `Muted list loaded for @${username} (${muted.size} account${muted.size === 1 ? '' : 's'}).`
      );
      analyze();
    } catch (fetchError) {
      const reason = fetchError instanceof Error ? fetchError.message : String(fetchError);
      setMuteStatusMessage(`Could not load muted list for @${username}: ${reason}`);
    }
  }, [analyze, keychainUsername]);

  const handleMuteAuthor = useCallback(
    (author: string) => {
      const keychain = window.hive_keychain;
      const username = keychainUsername.trim().replace(/^@/, '');

      if (!username) {
        setMuteStatusMessage('Enter your Hive username first to use mute actions.');
        return;
      }

      if (!keychain) {
        setMuteStatusMessage('Hive Keychain extension is not available in this browser.');
        return;
      }

      if (mutedAuthors.has(author.toLowerCase())) {
        setMuteStatusMessage(`@${author} is already muted by @${username}.`);
        return;
      }

      setMutingAuthor(author);
      setMuteStatusMessage(null);

      const json = JSON.stringify([
        'follow',
        {
          follower: username,
          following: author,
          what: ['ignore'],
        },
      ]);

      keychain.requestCustomJson(
        username,
        'follow',
        'Posting',
        json,
        `Mute @${author}`,
        (response) => {
          setMutingAuthor(null);

          if (response.success) {
            setMuteStatusMessage(`Muted @${author} as @${username}.`);
            setMutedAuthors((prev) => {
              const next = new Set(prev);
              next.add(author.toLowerCase());
              return next;
            });
            return;
          }

          const reason = response.message || response.error || 'unknown error';
          setMuteStatusMessage(`Failed to mute @${author}: ${reason}`);
        }
      );
    },
    [keychainUsername, mutedAuthors]
  );

  const filteredData = useMemo(() => {
    const query = filters.query.trim().toLowerCase();
    const minMatch = filters.minMatch === 'all' ? 0 : Number(filters.minMatch);

    return data.filter((row) => {
      const queryMatch =
        query.length === 0 ||
        row.author.toLowerCase().includes(query) ||
        row.hivePost.title.toLowerCase().includes(query) ||
        row.hivePost.permlink.toLowerCase().includes(query);

      if (!queryMatch) return false;
      if (filters.hideMuted && mutedAuthors.has(row.author.toLowerCase())) return false;
      if (!matchesStatus(row.steemitStatus, filters.steemit)) return false;
      if (!matchesStatus(row.blurtStatus, filters.blurt)) return false;
      return row.identityMatchScore >= minMatch;
    });
  }, [data, filters, mutedAuthors]);

  const cacheStatusLabel = useMemo(() => {
    if (!cacheInfo.generatedAt) {
      return 'Cache: not loaded yet';
    }

    const generated = new Date(cacheInfo.generatedAt);
    if (Number.isNaN(generated.getTime())) {
      return 'Cache: timestamp unavailable';
    }

    const diffMs = Date.now() - generated.getTime();
    const minutes = Math.max(0, Math.floor(diffMs / 60000));
    const sourceLabel =
      cacheInfo.source === 'server-cache'
        ? 'server cache hit'
        : cacheInfo.source === 'server-fresh'
          ? 'server refreshed'
          : 'client direct';

    const truncationSuffix =
      cacheInfo.truncated && cacheInfo.analyzedPosts && cacheInfo.totalRecentPosts
        ? `, truncated ${cacheInfo.analyzedPosts}/${cacheInfo.totalRecentPosts}`
        : '';

    return `Cache: ${minutes}m ago (${sourceLabel}${truncationSuffix})`;
  }, [cacheInfo]);

  const emptyMessage =
    data.length === 0
      ? 'No data loaded. Click "Analyze" to fetch recent posts.'
      : 'No results match current filters.';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex flex-col">
      <Header />

      <main className="flex-1">
        <div className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8 w-full">
          <KeychainMutePanel
            username={keychainUsername}
            onUsernameChange={setKeychainUsername}
            keychainAvailable={keychainAvailable}
            statusMessage={muteStatusMessage}
          />

          <div className="mb-4 text-xs text-slate-600 bg-white border border-slate-200 rounded-lg px-4 py-2">
            {cacheStatusLabel}
          </div>

          <ControlPanel
            onAnalyze={handleAnalyze}
            loading={loading}
            error={error}
            dataCount={data.length}
          />

          <FilterBar
            filters={filters}
            onChange={setFilters}
            onReset={() => setFilters(DEFAULT_FILTERS)}
            totalCount={data.length}
            filteredCount={filteredData.length}
          />

          <div className="mt-6">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">
              📊 Cross-Chain Analysis Results
            </h2>
            <DashboardTable
              data={filteredData}
              isLoading={loading.isLoading}
              emptyMessage={emptyMessage}
              canMute={canMute}
              mutingAuthor={mutingAuthor}
              mutedAuthors={mutedAuthors}
              onMuteAuthor={handleMuteAuthor}
            />
          </div>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg p-6 border border-slate-200 shadow-sm">
              <h3 className="font-semibold text-slate-900 mb-2">🎯 Match Score</h3>
              <p className="text-sm text-slate-600">
                Indicates probability of cross-posting based on permlink and title matching.
              </p>
              <div className="mt-3 space-y-1 text-xs">
                <p>✅ 80%+ = Likely cross-post</p>
                <p>⚠️ 50-79% = Possible match</p>
                <p>❌ &lt;50% = No match</p>
              </div>
            </div>

            <div className="bg-white rounded-lg p-6 border border-slate-200 shadow-sm">
              <h3 className="font-semibold text-slate-900 mb-2">📍 Activity Status</h3>
              <p className="text-sm text-slate-600">
                Shows author activity in the last 24 hours on each blockchain.
              </p>
              <div className="mt-3 space-y-1 text-xs">
                <p>✅ Active = Posted/voted in 24h</p>
                <p>❌ Inactive = No activity</p>
                <p>⚠️ Error = Network issue</p>
              </div>
            </div>

            <div className="bg-white rounded-lg p-6 border border-slate-200 shadow-sm">
              <h3 className="font-semibold text-slate-900 mb-2">🔗 Data Source</h3>
              <p className="text-sm text-slate-600">
                Real-time data from official blockchain RPC endpoints with fallback nodes.
              </p>
              <div className="mt-3 space-y-1 text-xs">
                <p>• HIVE: api.hive.blog</p>
                <p>• Steemit: api.steemit.com</p>
                <p>• Blurt: rpc.blurt.world</p>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

export default App;
